// verify.js — post-publish verification (plan Step 8 + red-team D2)
//
// After createRecord, poll app.bsky.feed.getPostThread (up to ~10s,
// backing off) and confirm the embed hydrates as a gallery view with N
// items. This is also how we empirically determine which CDN preset the
// carousel uses (plan §13 Q3): the viewImage thumbnail/fullsize URLs carry
// the preset in the first path segment (e.g. /feed_thumbnail/plain/...).
//
// D2 (owner 2026-08-19, option a): if the record was created but the embed
// does NOT hydrate (#viewNotFound / unhydrated), the CALLER deletes the
// record via deleteRecord and warns — a broken public post is worse than
// a deleted one.

import { sleep } from './rateLimit';

export const VERIFY_MAX_POLL_MS = 10000;
export const VERIFY_POLL_BASE_MS = 500;
export const VERIFY_POLL_MAX_BACKOFF_MS = 2000;

/**
 * Extract the CDN preset name from a viewImage URL.
 * URL shape: /{preset}/plain/{did}/{cid}@{format}
 * Returns the first path segment or null.
 */
export function presetFromUrl(url) {
  // Boundary parse: url is a string or null from the API.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (typeof url !== 'string') return null;

  try {
    const pathname = new URL(url).pathname; // e.g. /img/feed_thumbnail/plain/did/cid@jpeg
    const m = pathname.match(/^\/[^/]+\/([^/]+)\//);

    return m ? m[1] : null;
  } catch {
    return null;
  }
}

/**
 * Analyze one getPostThread response for gallery hydration.
 *
 * @param {Object} thread getPostThread response data ({thread: {...}})
 * @param {number} expectedItems expected slice count
 * @returns {{
 *   hydrated: boolean,
 *   failure: string|null,
 *   embedType: string|null,
 *   itemCount: number,
 *   viewItems: Array<{thumbnail:string, fullsize:string, preset:string|null}>,
 *   observedPresets: string[]
 * }}
 */
export function analyzeGalleryEmbed(thread, expectedItems) {
  const post = thread?.thread?.post;
  const embed = post?.embed;

  if (!post || !embed) {
    return {
      hydrated: false,
      failure: 'no-embed',
      embedType: null,
      itemCount: 0,
      viewItems: [],
      observedPresets: [],
    };
  }

  const embedType = embed.$type || null;

  // #viewNotFound is the AppView's signal that the embed is not live.
  if (embedType === 'app.bsky.embed.images#view' || embedType === 'app.bsky.embed.images') {
    return {
      hydrated: false,
      failure: 'wrong-embed-images',
      embedType,
      itemCount: Array.isArray(embed.images) ? embed.images.length : 0,
      viewItems: [],
      observedPresets: [],
    };
  }

  if (embedType !== 'app.bsky.embed.gallery#view') {
    return {
      hydrated: false,
      failure: `unexpected-embed-${embedType || 'unknown'}`,
      embedType,
      itemCount: 0,
      viewItems: [],
      observedPresets: [],
    };
  }

  const items = Array.isArray(embed.items) ? embed.items : [];

  const viewItems = items.map((item) => {
    const thumbnail = item?.thumbnail || '';
    const fullsize = item?.fullsize || '';

    return {
      thumbnail,
      fullsize,
      preset: presetFromUrl(thumbnail) || presetFromUrl(fullsize),
    };
  });

  const observedPresets = [...new Set(viewItems.map((v) => v.preset).filter(Boolean))];

  const hydrated = items.length === expectedItems && items.length >= 5;

  return {
    hydrated,
    failure: hydrated ? null : `item-count-${items.length}-expected-${expectedItems}`,
    embedType,
    itemCount: items.length,
    viewItems,
    observedPresets,
  };
}

/**
 * Poll getPostThread until the gallery embed hydrates or the window closes.
 *
 * @param {Object} args
 * @param {Agent} args.agent
 * @param {string} args.uri created post AT-URI
 * @param {number} args.expectedItems slice count
 * @param {number} [args.maxPollMs=10000]
 * @param {(attempt:number, analysis:Object) => void} [args.onAttempt]
 * @returns {Promise<{verified:boolean, analysis:Object, attempts:number}>}
 */
export async function verifyPost({ agent, uri, expectedItems, maxPollMs = VERIFY_MAX_POLL_MS, onAttempt }) {
  const deadline = Date.now() + maxPollMs;
  let attempt = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    attempt += 1;
    let analysis;

    try {
      const res = await agent.api.app.bsky.feed.getPostThread({ uri });
      analysis = analyzeGalleryEmbed(res.data, expectedItems);
    } catch (err) {
      // A 404 on the thread usually means the AppView has not indexed the
      // post yet — keep polling until the window closes.
      analysis = {
        hydrated: false,
        failure: `getPostThread-${err?.status ?? 'error'}`,
        embedType: null,
        itemCount: 0,
        viewItems: [],
        observedPresets: [],
      };
    }

    onAttempt?.(attempt, analysis);

    if (analysis.hydrated) {
      return { verified: true, analysis, attempts: attempt };
    }

    if (Date.now() >= deadline) {
      return { verified: false, analysis, attempts: attempt };
    }

    const backoff = Math.min(VERIFY_POLL_MAX_BACKOFF_MS, VERIFY_POLL_BASE_MS * 2 ** (attempt - 1));
    await sleep(backoff);
  }
}

/**
 * Delete a just-created post (D2 auto-delete on failed verification).
 * Returns the deleteRecord response data.
 */
export async function deletePost({ agent, did, uri }) {
  const rkey = uri.split('/').pop();

  const res = await agent.api.com.atproto.repo.deleteRecord({
    repo: did,
    collection: 'app.bsky.feed.post',
    rkey,
  });

  return res.data;
}